import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { subscribePaginated } from '@adornis/baseql/server/resolver/mongo.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import {
  getCurrentUserServer,
  getCurrentUserServerOrFail,
  loadRawEntityDataByID,
  loadRawEntityDataByIDOrFail,
  loadRawEntityDataByQuery,
  mongoUpdateMany,
  type ServerCachedUser,
  saveEntityData,
  validateAuthorization,
} from '@adornis/bh-util/util.js';
import type { PaymentStatus, SubscriptionStatus } from '@adornis/payments/db/payments.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { addMinutes } from 'date-fns';
import type { AnyBulkWriteOperation } from 'mongodb';
import { type Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { loadUserEntryRequirementsStatus } from '../api/product-entry-requirement.js';
import { getWaitinglistPriorityForUserForProduct } from '../api/product-waitinglist-priority-rule.js';
import { bhBaseQlContext } from '../bh-context.js';
import { TimeSlot } from '../datetime/time-slot.js';
import { getDbPluginForScope } from '../db-plugins.js';
import { BHPayment } from '../entities/BHPayment.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { AppointmentParticipation, ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { ProductParticipationConfirmationLink } from '../entities/ProductParticipationConfirmationLink.js';
import { Product } from '../entities/products.js';
import { BookingResult } from '../entities/RegistrationDataBuffer.js';
import { getMergedProductSettings } from '../entities/settings.js';
import type { Suspension } from '../entities/suspension.js';
import { Teaching } from '../entities/teaching.js';
import { lecturerContextsOf, User } from '../entities/user.js';
import { WorkshopAppointmentParticipationConfirmationLink } from '../entities/WorkshopAppointmentParticipationConfirmationLink.js';
import { mongo } from '../server/mongo.js';
import { mayViewWorkshopMeetingLinks } from '../server/meeting-link-access.js';
import { Workshop } from './../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { BlockSignoffDaysInAdvanceError } from '../errors/BlockSignoffDaysInAdvanceError.js';
import { getParticipationTimespan, participationCountsAsParallelSignup } from '../logic/participation.js';
import { sendEinzelberatungMeetingInfoMailsIfAlreadySent } from '../notification/einzelberatung-meeting-infos.js';
import { sendMeetingInfoMailsIfAlreadySent } from '../notification/meeting-infos.js';
import { notifyNachrueckplatzErhalten } from '../notification/nachrueckplatz-erhalten.js';
import { notifyNuzterSperrung } from '../notification/nutzer-sperrung.js';
import { notifyNuzterSperrungAufgehoben } from '../notification/nutzer-sperrung-aufgehoben.js';
import { notifyNuzterTeilnahme } from '../notification/nutzer-teilnahme.js';
import { notifyNuzterTeilnahmeAufgehoben } from '../notification/nutzer-teilnahme-aufgehoben.js';
import { notifyVeranstaltungseintragungDurchAdmin } from '../notification/veranstaltungseintragung-durch-admin.js';
import { createPayment } from '../payment.js';
import { getMeetingInfoLeadDays, isMeetingInfoReleased } from '../server/logic/appointments.js';
import { getEinzelberatungByID } from './einzelberatung.js';
import {
  failWhenSuspended,
  getAppointmentParticipationConformationSecret,
  getParticipationConformationSecret,
  workshopParticipationFullfillsParticipationRequirements,
} from '../server/logic/participation.js';
import { lookupEntityById, searchMatch, withIdTiebreaker } from '../util.js';
import { getParticipationsByID } from './participation-helper.js';
import { getUserByID, getAllowedUsers } from './users.js';
import { calculateWorkshopLeftCapacity, getWorkshopByID } from './workshop.js';
import { getProductByID } from './product.js';

@Entity()
export class AppointmentMeetingAccess extends AdornisEntity {
  static override _class = 'AppointmentMeetingAccess';

  @Field(type => ID) appointmentID: Maybe<string>;
  @Field(type => String) link: Maybe<string>;
  @Field(type => String) code: Maybe<string>;
  @Field(type => String) password: Maybe<string>;
}

export function filterAccessibleMeetingInfos(
  state: ParticipationState,
  workshop: NarrowedEntity<
    Workshop,
    {
      _id: 1;
      isCanceled: 1;
      settings: { meetingInfoMailLeadTime: 1; inheritedMeetingInfoMailLeadTime: 1 };
      appointments: { _id: 1; start: 1; meetingLink: { link: 1; code: 1; password: 1 } };
    }
  >,
  now: Date,
): Pick<AppointmentMeetingAccess, 'appointmentID' | 'link' | 'code' | 'password'>[] {
  return (workshop.appointments ?? []).flatMap(appointment => {
    if (!isMeetingInfoReleased({
      state,
      isCanceled: workshop.isCanceled,
      leadDays: getMeetingInfoLeadDays(workshop),
      appointmentStart: appointment.start,
      now,
    })) return [];

    const { link, code, password } = appointment.meetingLink ?? {};
    if (!link && !code && !password) return [];
    return [{ appointmentID: appointment._id, link, code, password }];
  });
}

export const getMyAppointmentMeetingAccess = registerQuery({
  operationName: 'getMyAppointmentMeetingAccess',
  type: () => [AppointmentMeetingAccess],
  params: [{ name: 'participationID', type: () => ID }],
  resolve: (participationID: string) => async (gqlFields) => {
    if (context.serverContext) return [];
    const userID = context.userID as string;
    if (!userID) return [];

    // Both booking types share the collection; this selection also includes the counseling slot ID.
    const participation = await loadRawEntityDataByID(ParticipationEinzelberatung, participationID, {
      _id: 1,
      _class: 1,
      userID: 1,
      productID: 1,
      state: 1,
      timeslotID: 1,
    });
    if (!participation || participation.userID !== userID) return [];
    if (![ParticipationState.in, ParticipationState.participated].includes(participation.state)) return [];

    if (participation._class === ParticipationEinzelberatung._class) {
      const counseling = await runInContext(serverContext.sessionID, () => getEinzelberatungByID(participation.productID)({
        isCanceled: 1,
        slots: { _id: 1, start: 1, beraterID: 1 },
        slotConfig: { beraterMeetingLinks: { beraterID: 1, meetingLink: { link: 1 } } },
      }));
      const slot = counseling?.slots?.find(slot => slot._id === participation.timeslotID);
      if (!counseling || !slot) return [];
      const settings = await getMergedProductSettings(participation.productID, ['einzelberatungMeetingInfoMailLeadTime']);
      if (!isMeetingInfoReleased({
        state: participation.state,
        isCanceled: counseling.isCanceled,
        leadDays: settings.einzelberatungMeetingInfoMailLeadTime ?? null,
        appointmentStart: slot.start,
        now: new Date(),
      })) return [];
      const link = counseling.slotConfig?.beraterMeetingLinks?.find(info => info.beraterID === slot.beraterID)?.meetingLink?.link;
      if (!link) return [];
      return [new AppointmentMeetingAccess({ appointmentID: participation.timeslotID, link, code: null, password: null })];
    }

    const workshop = await runInContext(serverContext.sessionID, () => getWorkshopByID(participation.productID)({
      _id: 1,
      _class: 1,
      isCanceled: 1,
      settings: { meetingInfoMailLeadTime: 1, inheritedMeetingInfoMailLeadTime: 1 },
      appointments: { _id: 1, start: 1, meetingLink: { link: 1, code: 1, password: 1 } },
    }));
    if (!workshop || workshop._class !== Workshop._class) return [];

    return filterAccessibleMeetingInfos(participation.state, workshop, new Date()).map(
      info => new AppointmentMeetingAccess(info),
    );
  },
});

async function setAppointmentParticipationServer(participationID: string, appointmentID: string, fullfilled: boolean) {
  logger.info({ participationID, appointmentID, fullfilled }, 'setAppointmentParticipationServer');

  const collection = mongo().collection<EntityData<ParticipationWorkshop>>(Participation._collectionName);

  if (fullfilled) {
    const entry = new AppointmentParticipation({ appointmentID }).toJSON() as any;
    const res = await collection.updateOne(
      { _id: participationID, 'appointmentParticipations.appointmentID': { $ne: appointmentID } },
      { $push: { appointmentParticipations: entry }, $set: { updatedAt: new Date() } },
    );
    // matchedCount==0 is ambiguous (missing participation vs. already-present entry); disambiguate.
    if (res.matchedCount === 0) {
      const exists = await collection.countDocuments({ _id: participationID }, { limit: 1 });
      if (exists === 0) throw new Error(`Participation not found: ${participationID}`);
    }
  } else {
    const res = await collection.updateOne(
      { _id: participationID },
      { $pull: { appointmentParticipations: { appointmentID } }, $set: { updatedAt: new Date() } },
    );
    if (res.matchedCount === 0) throw new Error(`Participation not found: ${participationID}`);
  }

  // admin should be able to set state manually
  if (bhBaseQlContext.participationStateChangeWasInitiatedByAdmin) return true;

  await maybeTransitionWorkshopParticipationStateAfterAppointmentChange(participationID);

  return true;
}

async function maybeTransitionWorkshopParticipationStateAfterAppointmentChange(participationID: string) {
  const participation = await loadRawEntityDataByID(ParticipationWorkshop, participationID, {
    _id: 1,
    _class: 1,
    state: 1,
    productID: 1,
    fulfilledParticipationRequirements: 1,
    appointmentParticipations: { _id: 1, _class: 1, appointmentID: 1, date: 1 },
  });
  if (!participation) return;

  if (participation.state !== ParticipationState.participated && participation.state !== ParticipationState.in) return;

  const product = await loadRawEntityDataByIDOrFail(Workshop, participation.productID, {
    appointments: { _id: 1, isNonBlocking: 1 },
  });
  const productSettings = await getMergedProductSettings(participation.productID, ['extraParticipationConstraints']);

  const requirementsMet = workshopParticipationFullfillsParticipationRequirements({
    ...participation,
    product: { ...product, settings: productSettings },
  });
  const stateShould = requirementsMet ? ParticipationState.participated : ParticipationState.in;
  if (stateShould !== participation.state) {
    await setParticipatedByID(participationID, requirementsMet);
  }
}

export const setAppointmentParticipation = registerMutation({
  operationName: 'setAppointmentParticipation',
  type: () => Boolean,
  params: [
    { name: 'participationID', type: () => String },
    { name: 'appointmentID', type: () => String },
    { name: 'fullfilled', type: () => Boolean },
  ],
  resolve: (participationID: string, appointmentID: string, fullfilled: boolean) => async () => {
    if (context.serverContext) return;
    const contextUserID = context.userID as string;
    if (!contextUserID) throw new Error('user not logged in');
    const currentUser = await getCurrentUserServerOrFail();
    if (
      !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION) &&
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION)
    )
      throw new Error('You dont have enough permission!');

    return setAppointmentParticipationServer(participationID, appointmentID, fullfilled);
  },
});

@Entity()
export class AppointmentParticipationForBulkUpdate extends AdornisEntity {
  static override _class = 'AppointmentParticipationForBulkUpdate';

  @Field(type => String) participationID: Maybe<string>;
  @Field(type => String) appointmentID: Maybe<string>;
  @Field(type => Boolean) fullfilled: Maybe<boolean>;
}

export const setAppointmentParticipationBulk = registerMutation({
  operationName: 'setAppointmentParticipationBulk',
  type: () => Boolean,
  params: [{ name: 'participations', type: () => [AppointmentParticipationForBulkUpdate] }],
  resolve: (participations: AppointmentParticipationForBulkUpdate[]) => async () => {
    if (context.serverContext) return;
    const contextUserID = context.userID as string;
    if (!contextUserID) throw new Error('user not logged in');
    const currentUser = await getCurrentUserServerOrFail();
    if (
      !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION) &&
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION)
    )
      throw new Error('You dont have enough permission!');

    bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = false; // this action is intiallized by admin, but participation state should still update automatically

    const grouped = new Map<string, { pushIDs: Set<string>; pullIDs: Set<string> }>();
    for (const it of participations) {
      if (!it.participationID || !it.appointmentID) continue;
      let entry = grouped.get(it.participationID);
      if (!entry) {
        entry = { pushIDs: new Set(), pullIDs: new Set() };
        grouped.set(it.participationID, entry);
      }
      if (it.fullfilled) {
        entry.pushIDs.add(it.appointmentID);
        entry.pullIDs.delete(it.appointmentID);
      } else {
        entry.pullIDs.add(it.appointmentID);
        entry.pushIDs.delete(it.appointmentID);
      }
    }

    if (grouped.size === 0) return;

    const collection = mongo().collection<EntityData<ParticipationWorkshop>>(Participation._collectionName);
    const now = new Date();
    const ops: AnyBulkWriteOperation<EntityData<ParticipationWorkshop>>[] = [];
    for (const [participationID, { pushIDs, pullIDs }] of grouped) {
      if (pullIDs.size > 0) {
        ops.push({
          updateOne: {
            filter: { _id: participationID },
            update: {
              $pull: { appointmentParticipations: { appointmentID: { $in: [...pullIDs] } } } as any,
              $set: { updatedAt: now },
            },
          },
        });
      }
      for (const appointmentID of pushIDs) {
        ops.push({
          updateOne: {
            filter: { _id: participationID, 'appointmentParticipations.appointmentID': { $ne: appointmentID } },
            update: {
              $push: { appointmentParticipations: new AppointmentParticipation({ appointmentID }).toJSON() as any },
              $set: { updatedAt: now },
            },
          },
        });
      }
    }

    if (ops.length > 0) await collection.bulkWrite(ops);

    await Promise.all(
      [...grouped.keys()].map(id => maybeTransitionWorkshopParticipationStateAfterAppointmentChange(id)),
    );
  },
});

export const setParticipationRequirement = registerMutation({
  operationName: 'setParticipationRequirement',
  type: () => Boolean,
  params: [
    { name: 'participationID', type: () => String },
    { name: 'requirement', type: () => String },
    { name: 'fullfilled', type: () => Boolean },
    { name: 'throughAdmin', type: () => Boolean },
  ],
  resolve: (participationID: string, requirement: string, fullfilled: boolean, throughAdmin: boolean) => async () => {
    if (context.serverContext) return;
    const contextUserID = context.userID as string;
    if (!contextUserID) throw new Error('user not logged in');
    const currentUser = await getCurrentUserServerOrFail();
    if (
      !currentUser.hasPermission(Permission.EDIT_COUNSELING_PARTICIPATION) &&
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION)
    )
      throw new Error('You dont have enough permission!');

    const participation = (await getParticipationByID(participationID)({
      _id: 1,
      fulfilledParticipationRequirements: 1,
      state: 1,
      appointmentParticipations: { _id: 1, appointmentID: 1 },
      productID: 1,
      product: { settings: { extraParticipationConstraints: 1 }, appointments: { _id: 1, isNonBlocking: 1 } },
    })) as ParticipationWorkshop | null;

    if (!participation) throw new Error(`No participation to participate in found (${participationID})`);

    // add/remove fullfilledRequirement
    participation.fulfilledParticipationRequirements = (() => {
      if (fullfilled) return [...(participation.fulfilledParticipationRequirements ?? []), requirement];
      else return (participation.fulfilledParticipationRequirements ?? []).filter(it => it !== requirement);
    })();

    // filter duplicates
    participation.fulfilledParticipationRequirements = [...new Set(participation.fulfilledParticipationRequirements)];

    // remove unknown requirements
    const allKnownRequirements = participation.product?.settings?.extraParticipationConstraints ?? [];
    participation.fulfilledParticipationRequirements = participation.fulfilledParticipationRequirements.filter(it =>
      allKnownRequirements.includes(it),
    );

    // save
    await participation.save();

    // maybe update state (and trigger sideeffects)
    if (throughAdmin) return true; // admin should be able to set state manually
    if (participation.state === ParticipationState.participated || participation.state === ParticipationState.in) {
      const requirementsMet = workshopParticipationFullfillsParticipationRequirements(participation);
      const stateShould = requirementsMet ? ParticipationState.participated : ParticipationState.in;
      if (stateShould !== participation.state) {
        await setParticipatedByID(participationID, requirementsMet);
      }
    }
  },
});

const MAX_BULK_PARTICIPATIONS = 10000;

async function getAuthorizedBulkParticipations(participationIDs: string[], permissions: Permission[]) {
  const currentUser = await getCurrentUserServerOrFail();
  const uniqueIDs = [...new Set(participationIDs)];
  if (!uniqueIDs.length) throw new ExpectedError('participationIDs must not be empty');
  if (uniqueIDs.length > MAX_BULK_PARTICIPATIONS) throw new ExpectedError('too many participations');

  const collection = mongo().collection<EntityData<Participation>>(Participation._collectionName);
  const participations = await collection
    .find({ _id: { $in: uniqueIDs } }, { projection: { _id: 0, productID: 1 } })
    .toArray();
  if (participations.length !== uniqueIDs.length) throw new ExpectedError('not authorized');
  const productIDs = [...new Set(participations.map(participation => participation.productID))];
  if (productIDs.length !== 1) throw new ExpectedError('not authorized');
  const productID = productIDs[0]!;

  const product = await mongo()
    .collection<EntityData<Product>>(Product._collectionName)
    .findOne({ _id: productID }, { projection: { institutionID: 1 } });
  if (!product || !permissions.some(permission => currentUser.hasPermission(permission, product.institutionID)))
    throw new ExpectedError('not authorized');

  return { collection, productID, uniqueIDs };
}

export const setParticipationRequirementBulk = registerMutation({
  operationName: 'setParticipationRequirementBulk',
  type: () => Int,
  params: [
    { name: 'participationIDs', type: () => [String] },
    { name: 'requirement', type: () => String },
    { name: 'fullfilled', type: () => Boolean },
  ],
  resolve: (participationIDs: string[], requirement: string, fullfilled: boolean) => async () => {
    if (context.serverContext) return 0;
    const { collection, productID, uniqueIDs } = await getAuthorizedBulkParticipations(participationIDs, [
      Permission.MANAGE_PARTICIPATION,
      Permission.EDIT_COUNSELING_PARTICIPATION,
    ]);

    const productSettings = await getMergedProductSettings(productID, ['extraParticipationConstraints']);
    if (!productSettings.extraParticipationConstraints?.includes(requirement))
      throw new ExpectedError('unknown participation requirement');

    const result = await collection.updateMany(
      { _id: { $in: uniqueIDs }, productID },
      fullfilled
        ? { $addToSet: { fulfilledParticipationRequirements: requirement }, $set: { updatedAt: new Date() } }
        : { $pull: { fulfilledParticipationRequirements: requirement }, $set: { updatedAt: new Date() } },
    );
    return result.modifiedCount;
  },
});

export async function setParticipatedByID(participationID: string, value: boolean = true) {
  logger.info({ participationID, value }, 'setParticipatedByID');

  const state = value ? ParticipationState.participated : ParticipationState.in;

  // `state !== targetState` precondition closes the race where two concurrent callers both
  // passed the outer guard; the loser no-ops instead of re-firing mails and waiting-list recompute.
  const participationCollection = mongo().collection<EntityData<Participation>>(Participation._collectionName);
  const updateResult = await participationCollection.updateOne(
    { _id: participationID, state: { $ne: state } },
    { $set: { state, waitingListPlace: null, updatedAt: new Date() } },
  );
  if (updateResult.matchedCount === 0) {
    const exists = await participationCollection.countDocuments({ _id: participationID }, { limit: 1 });
    if (exists === 0) throw new Error(`Participation not found: ${participationID}`);
    return;
  }

  const baseParticipationData = await loadRawEntityDataByIDOrFail(Participation, participationID, {
    _id: 1,
    _class: 1,
    productID: 1,
    userID: 1,
    state: 1,
    gotCertificate: 1,
  });

  const productID = baseParticipationData.productID;
  const baseProduct = await loadRawEntityDataByIDOrFail(Product, productID, {
    _id: 1,
    attachedCertificate: { certificateID: 1 },
  });

  const productSettings = await getMergedProductSettings(productID, ['autoSendCertifatesEnabled']);

  const participationData = { ...baseParticipationData, product: { ...baseProduct, settings: productSettings } };
  participationData.state = state;

  // NOTE: recalculate nachruecklist if user has other participation with nachrueckPriority = "entry_requirements_not_met"
  //       because requirement could be fullfilled now
  const notFullfilledParticipations = await loadRawEntityDataByQuery(Participation, {
    userID: participationData.userID,
    state: ParticipationState.waiting,
    nachrueckPriority: 'entry_requirements_not_met',
  });

  if (notFullfilledParticipations.length > 0) {
    const entryReqStatus = await loadUserEntryRequirementsStatus(
      participationData.userID,
      notFullfilledParticipations.map(p => p.productID),
    );

    const recomputed = await Promise.all(
      notFullfilledParticipations.map(async p => ({
        participation: p,
        newPriority: await getWaitinglistPriorityForUserForProduct(
          p.userID,
          p.productID,
          entryReqStatus.get(p.productID),
        ),
      })),
    );

    const changed = recomputed.filter(
      r => r.newPriority !== 'entry_requirements_not_met' && r.newPriority !== r.participation.nachrueckPriority,
    );

    if (changed.length > 0) {
      // Deliberately do NOT touch `updatedAt` here: nachrueckPriority is an automated waitlist
      // recompute (runs on change streams and cascades from nightly jobs), not a change the person
      // or an admin made. Bumping updatedAt made the "letzte Änderung am" column show the cron time.
      await participationCollection.bulkWrite(
        changed.map(({ participation, newPriority }) => ({
          updateOne: { filter: { _id: participation._id }, update: { $set: { nachrueckPriority: newPriority } } },
        })),
      );
      // NOTE: nachrueckPriority changes are now handled by the change stream listener in waitinglist-monitoring.ts
    }
  }

  const instructions = shouldSendParticipationMailAfterParticipationParticipatedGotUpdated(participationData);

  if (instructions.shouldSendMail) {
    let mailSent = false;
    switch (instructions.mail) {
      case 'nutzer-teilnahme-aufgehoben':
        await notifyNuzterTeilnahmeAufgehoben(participationID);
        break;
      case 'nutzer-teilnahme-without-certificate':
        await notifyNuzterTeilnahme(participationID, false);
        break;
      case 'nutzer-teilnahme':
        mailSent = await notifyNuzterTeilnahme(participationID, true);
        break;
      default:
        throw new Error('unreachable');
    }

    if (instructions.mail === 'nutzer-teilnahme' && mailSent) {
      participationData.gotCertificate = new Date();
      await saveEntityData(participationData);
    }
  }
}

// Participants can click this link to confirm that they participated at an appointment
export const getWorkshopAppointmentParticipationConfirmationLink = registerQuery({
  type: () => [WorkshopAppointmentParticipationConfirmationLink],
  operationName: 'getWorkshopAppointmentParticipationConfirmationLink',
  params: [{ type: () => String, name: 'workshopID' }],
  resolve: (workshopID: string) => {
    return async (gqlFields: BaseQLSelectionSet<WorkshopAppointmentParticipationConfirmationLink>) => {
      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
        _id: 1,
        institutionID: 1,
        appointments: { _id: 1, start: 1, end: 1 },
      });
      if (!(await mayViewWorkshopMeetingLinks(context, workshop))) throw new Error('not authorized');

      const ws = await getMergedProductSettings(workshopID, [
        'qrCodeValidityInMinutes',
        'inheritedQrCodeValidityInMinutes',
      ]);

      const links: WorkshopAppointmentParticipationConfirmationLink[] = [];
      for (const appointment of workshop.appointments) {
        const token = getAppointmentParticipationConformationSecret(workshopID, appointment);
        const url = A.absoluteUrl(
          `/extern/confirm-appointment-participation` +
            `?workshopID=${workshopID}` +
            `&appointmentID=${appointment._id}` +
            `&token=${token}`,
        );

        const validityInMinutes = (() => {
          const minutes = ws.qrCodeValidityInMinutes ?? ws.inheritedQrCodeValidityInMinutes?.[1];
          if (!minutes) return null;
          // @ts-expect-error clean up settings api
          return parseInt(minutes);
        })();

        const validFrom = appointment.start;
        const validTill = validityInMinutes && appointment.end ? addMinutes(appointment.end, validityInMinutes) : null;
        links.push(
          new WorkshopAppointmentParticipationConfirmationLink({
            workshopID,
            appointmentID: appointment._id,
            validFrom: validFrom,
            validTill: validTill,
            url,
          }),
        );
      }

      return links;
    };
  },
});

export const handleWorkshopAppointmentParticipationConfirmation = registerMutation({
  operationName: 'handleWorkshopAppointmentParticipationConfirmation',
  type: () => Boolean,
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'appointmentID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (workshopID: string, appointmentID: string, token: string) => async () => {
    const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
      institutionID: 1,
      name: 1,
      appointments: { _id: 1, name: 1, start: 1, end: 1 },
    });

    const workshopSettings = await getMergedProductSettings(workshopID, ['qrCodeValidityInMinutes']);

    const appointment = workshop.appointments.find(it => it._id === appointmentID);
    if (!appointment) throw new Error('appointment not found ' + appointmentID);
    const user = await getCurrentUserServerOrFail();
    const participations = await loadRawEntityDataByQuery(
      Participation,
      { userID: user._id, productID: workshopID },
      { _id: 1, state: 1 },
    );
    if (participations.length > 1)
      throw new Error('there should be only one participation userID: ' + user._id + ' workshopID: ' + workshopID);
    const participation = participations[0];
    if (!participation)
      throw new ExpectedError('participation not found userID: ' + user._id + ' workshopID: ' + workshopID);
    if (participation.state !== ParticipationState.in && participation.state !== ParticipationState.participated)
      throw new Error('only_active_participatns_allowed');

    // check for valid token
    const secret = getAppointmentParticipationConformationSecret(workshopID, appointment);
    if (secret !== token) throw new ExpectedError('invalid_token');

    // check for valid time
    const now = new Date();
    if (appointment.start && now.getTime() < appointment.start.getTime()) {
      throw new Error('not_valid_yet');
    }

    const validityInMinutes = workshopSettings.qrCodeValidityInMinutes;

    if (typeof validityInMinutes === 'number') {
      const validityinMillis = validityInMinutes * 60 * 1000;

      if (appointment.end && appointment.end.getTime() + validityinMillis < now.getTime())
        throw new ExpectedError('expired');
    }

    await setAppointmentParticipationServer(participation._id, appointmentID, true);

    logger.info(
      {
        userID: user._id,
        email: user.email,
        institutionID: workshop.institutionID,
        workshopID,
        workshopName: workshop.name,
        appointmentID,
        appointmentName: appointment.name,
        scope: 'qr-scan',
        kind: 'appointment',
      },
      'qr_scan_appointment_confirmed',
    );

    return true;
  },
});

export const getProductParticipationConfirmationLink = registerQuery({
  type: () => ProductParticipationConfirmationLink,
  operationName: 'getProductParticipationConfirmationLink',
  params: [{ type: () => String, name: 'productID' }],
  resolve: (productID: string) => {
    return async (gqlFields: BaseQLSelectionSet<ProductParticipationConfirmationLink>) => {
      const product = await getProductByID(productID)({
        settings: { qrCodeValidityInMinutes: 1, inheritedQrCodeValidityInMinutes: 1 },
      });
      const ws = product?.settings;
      if (!product) throw new Error('product not found ' + productID);
      if (!ws) throw new Error('product settings not found ' + productID);

      const token = getParticipationConformationSecret(productID);
      const url = A.absoluteUrl(
        `/extern/confirm-product-participation` + `?productID=${productID}` + `&token=${token}`,
      );

      return new ProductParticipationConfirmationLink({ productID, url });
    };
  },
});

export const handleProductParticipationConfirmation = registerMutation({
  operationName: 'handleProductParticipationConfirmation',
  type: () => Boolean,
  params: [
    { name: 'productID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (productID: string, token: string) => async () => {
    const product = await loadRawEntityDataByIDOrFail(Product, productID, { _id: 1, institutionID: 1, name: 1 });

    const user = await getCurrentUserServer();
    if (!user) throw new Error('user not logged in');

    const participations = await loadRawEntityDataByQuery(Participation, { userID: user._id, productID: productID });
    if (participations.length > 1)
      throw new Error('there should be only one participation userID: ' + user._id + ' workshopID: ' + productID);
    const participation = participations[0];
    if (!participation) throw new Error('participation not found userID: ' + user._id + ' workshopID: ' + productID);
    if (participation.state !== ParticipationState.in && participation.state !== ParticipationState.participated)
      throw new Error('only_active_participatns_allowed');

    // check for valid token
    const secret = getParticipationConformationSecret(productID);
    if (secret !== token) throw new ExpectedError('invalid_token');

    // @ts-expect-error Sanity check for suspension
    if (participation.state === ParticipationState.suspended) throw new Error('participation is suspended');

    await setParticipatedByID(participation._id, true);

    logger.info(
      {
        userID: user._id,
        email: user.email,
        institutionID: product.institutionID,
        workshopID: productID,
        workshopName: product.name,
        appointmentID: null,
        scope: 'qr-scan',
        kind: 'product',
      },
      'qr_scan_product_confirmed',
    );

    return true;
  },
});

export const countWaitingByWorkshopServer = async (
  workshopID: string,
  priority?: Participation['nachrueckPriority'],
) => {
  const collection = mongo().collection<Participation>(Participation._collectionName);
  const count = await collection.countDocuments({
    state: { $in: [ParticipationState.waiting] },
    productID: workshopID,
    ...(priority ? { nachrueckPriority: priority } : {}),
  });
  return count;
};

export const countWaitingByWorkshop = registerQuery({
  operationName: 'countWaitingByWorkshop',
  params: [{ type: () => String, name: 'workshopID' }],
  type: () => Int,
  resolve: (workshopID: string) => () => countWaitingByWorkshopServer(workshopID),
});

export const subscribeParticipationsActiveByUser = registerSubscription({
  operationName: 'subscribeParticipationsActiveByUser',
  params: [{ type: () => String, name: 'userId' }],
  type: () => [Participation],
  middlewares: [checkAuthorizedMiddleware(({ user, params }) => user._id === params.userId)],
  resolve: (userID: string) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      return watchQuery(Participation._collectionName, { userID }, { projection: { _id: 1 } }).pipe(
        switchMap(async () => {
          const aggregation = [{ $match: { userID } }] as any;

          if (gqlFields.product) {
            aggregation.push({
              $lookup: { from: Product._collectionName, localField: 'productID', foreignField: '_id', as: 'product' },
            });
            aggregation.push({ $unwind: '$product' });
          }

          if (gqlFields.user) {
            aggregation.push({
              $lookup: { from: User._collectionName, localField: 'userID', foreignField: '_id', as: 'user' },
            });
            aggregation.push({ $unwind: '$user' });
          }

          const collection = mongo().collection<Participation>(Participation._collectionName);
          const result = await collection.raw.aggregate<Participation>(aggregation).toArray();

          if (gqlFields.product?.settings) {
            // TODO: use aggregatedFields for this when its ready
            const settingsFields = Object.keys(gqlFields.product.settings) as any;
            const uniqueProductIDs = Array.from(new Set(result.map(p => p.productID)));
            const settingsByProductID = new Map<string, any>();
            await Promise.all(
              uniqueProductIDs.map(async productID => {
                settingsByProductID.set(productID, await getMergedProductSettings(productID, settingsFields));
              }),
            );
            for (const participation of result) {
              // @ts-expect-error
              participation.product.settings = settingsByProductID.get(participation.productID);
            }
          }

          return result;
        }),
      );
    };
  },
});

export const subscribeAllUserParticipationsThatCurrentlyCountAsParallelSignup = registerSubscription({
  operationName: 'subscribeAllUserParticipationsThatCurrentlyCountAsParallelSignup',
  params: [{ type: () => String, name: 'userId' }],
  type: () => [Participation],
  middlewares: [checkAuthorizedMiddleware(({ user, params }) => user._id === params.userId)],
  resolve: (userID: string) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      return watchQuery(Participation._collectionName, { userID }).pipe(
        switchMap(async () => {
          const collection = mongo().collection<Participation>(Participation._collectionName);
          const _participations = await collection.raw
            .aggregate<Participation>([
              { $match: { userID } },
              {
                $lookup: { from: Product._collectionName, localField: 'productID', foreignField: '_id', as: 'product' },
              },
              { $unwind: '$product' },
              {
                $project: {
                  _id: 1,
                  _class: 1,
                  state: 1,
                  productID: 1,
                  userID: 1,
                  product: {
                    _id: 1,
                    _class: 1,
                    institutionID: 1,
                    appointments: { _id: 1, end: 1 },
                    slotConfig: { end: 1 },
                  },
                },
              },
            ])
            .toArray();

          return _participations.filter(p => participationCountsAsParallelSignup(p));
        }),
      );
    };
  },
});

export const subscribeParticipationsByUserPaginated = registerSubscription({
  operationName: 'subscribeParticipationsByUserPaginated',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => BHFilter, name: 'filter' },
  ],
  type: () => [Participation],
  middlewares: [checkAuthorizedMiddleware(({ user, params }) => user._id === params.filter.userID)],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      if (!filter.userID) return of([]);
      return subscribePaginated(Participation, skip, limit, { userID: filter.userID });
    };
  },
});

export const subscribeParticipationsByInstitution = registerSubscription({
  operationName: 'subscribeParticipationsByInstitution',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => BHFilter, name: 'filter' },
  ],
  type: () => [Participation],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, params.filter.institutionID),
    ),
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Participation>) => {
      const institutionID = filter.institutionID;
      if (!institutionID) throw new Error('institutionID is required');

      gqlFields.productID = 1;

      const timeframeFilter = filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {};
      const searchFields = ['user.firstName', 'user.lastName', 'user.email', 'product.name'];
      const aggregation = [
        { $match: { 'product.institutionID': institutionID, ...timeframeFilter } },
        {
          $set: {
            institutionID: '$product.institutionID',
            timeframeID: '$product.timeframeID',
            startAt: { $ifNull: [{ $min: '$product.appointments.start' }, '$product.slotConfig.start'] },
          },
        },
        { $match: searchMatch(searchFields, filter.search) },
        { $match: filter?.getMongoFilter() ?? {} },
        { $sort: withIdTiebreaker(filter?.getMongoOptions().sort) },
        { $skip: skip },
        { $limit: limit },
      ];

      return watchEntireCollection(`${Participation._collectionName}-materialized`).pipe(
        switchMap(() => Promise.resolve(mongo().collection(`${Participation._collectionName}-materialized`))),
        switchMap(collection =>
          collection.raw.aggregate<Participation>(aggregation, { collation: { locale: 'de' } }).toArray(),
        ),
      );
    };
  },
});

export async function getParticipationCurrentPaymentServer(participationID: string) {
  const paymentCollection = mongo().collection(BHPayment._collectionName);
  const payments = await paymentCollection
    .find({ participationIDs: participationID })
    .sort({ createdAt: -1 })
    .toArray();
  return payments.at(0) as Maybe<EntityData<BHPayment>>; // return latest payment
}

export const getParticipationPayment = registerQuery({
  operationName: 'getParticipationPayment',
  params: [
    { type: () => String, name: 'participationID' },
    { type: () => Boolean, name: 'includeWaiting' },
  ],
  type: () => [String, Int, String],
  resolve:
    (participationID: string, includeWaiting: boolean = false) =>
    async () => {
      const user = await getCurrentUserServerOrFail();

      const participation = await loadRawEntityDataByIDOrFail(Participation, participationID, {
        _id: 1,
        userID: 1,
        productID: 1,
        state: 1,
      });

      const product = await loadRawEntityDataByIDOrFail(Product, participation.productID, {
        _id: 1,
        institutionID: 1,
        prices: { unitAmount: 1 },
      });

      // check permission
      await validateAuthorization(user => {
        if (user._id === participation.userID) return true;
        if (user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, product.institutionID)) return true;
        return false;
      });

      // when waiting no payment exists, so we return the price instead (this get used on profil page but should mayne get handled diffrently)
      if (participation.state === ParticipationState.waiting) {
        if (!includeWaiting) return undefined;
        const institutionID = product.institutionID;
        const paymentPlugin = getDbPluginForScope('payment', institutionID);

        const price = await paymentPlugin.getProductPrice(product._id, user._id);
        return ['created', price?.unitAmount, null];
      }

      const c = mongo().collection<BHPayment>(BHPayment._collectionName);
      const payments = await c.find({ participationIDs: participationID }).toArray();
      const paymentStatusOrder: Array<PaymentStatus | SubscriptionStatus> = [
        'created',
        'issued',
        'expired',
        'failed',
        'canceled',
        'paid',
        'refund',
      ];
      let highestStatusPayment: (typeof payments)[0] | undefined;
      payments.forEach(payment => {
        const idx = paymentStatusOrder.indexOf(payment.status);
        if (!highestStatusPayment || idx > paymentStatusOrder.indexOf(highestStatusPayment.status)) {
          highestStatusPayment = payment;
        }
      });

      if (!highestStatusPayment) return undefined;

      const payment = await getParticipationCurrentPaymentServer(participation._id);
      if (!payment) throw new Error('should not happen');

      // TODO: find out why sometimes basicProduct.reference and sometimes basicProduct._id is set
      const getBasicProductRef = basicProduct => basicProduct.reference ?? basicProduct._id;

      const chosenProduct = payment.products?.find(p => getBasicProductRef(p.product) === participation.productID);

      const basicProduct = chosenProduct?.product;
      if (!basicProduct) throw new Error('basicPoduct not found');

      const price = chosenProduct.product.prices.find(price => price._id === chosenProduct.priceID);
      return [highestStatusPayment.status, price?.unitAmount, payment._id];
    },
});

export const removeParticipationsByUserAndInstitution = registerMutation({
  operationName: 'removeParticipationsByUserAndInstitution',
  params: [
    { type: () => String, name: 'userID' },
    { type: () => String, name: 'institutionID' },
  ],
  type: () => String,
  resolve: (userID: string, institutionID: string) => {
    return async () => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) throw new Error('user not logged in');
      const currentUser = await getUserByID(contextUserID)({
        roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
      });
      if (!currentUser) throw new Error('user not logged in');
      const hasPermission = currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_USERS, institutionID);
      if (!hasPermission) throw new Error('no permission to do that');

      const collectionParticipation = mongo().collection<Participation>(Participation._collectionName);
      const { Product } = await import('../entities/products.js');

      // remove participations
      const participationsAggregated = await collectionParticipation.raw
        .aggregate<Participation>([
          { $match: { userID } },
          { $lookup: { from: Product._collectionName, localField: 'productID', foreignField: '_id', as: 'product' } },
          { $match: { 'product.institutionID': institutionID } },
          { $project: { _id: 1 } },
        ])
        .toArray();

      const ids = participationsAggregated.map(data => data._id);

      await collectionParticipation.deleteMany({ _id: { $in: ids } });
    };
  },
});

export async function _cancelEnrollment(
  participation: NarrowedEntityData<
    Participation,
    {
      _id: 1;
      _class: 1;
      productID: 1;
      state: 1;
      lastStateChangeWasInitiatedByAdmin: 1;
      timeslotID: 1;
      waitingListPlace: 1;
      nachrueckPriority: 1;
      user: { suspentions: { institutionID: 1 } };
      product: { _id: 1; institutionID: 1; settings: { blockSignOffDaysInAdvance: 1 } };
    }
  >,
  throughAdmin: boolean,
) {
  // check if cancel possible
  const product = participation.product;
  if (!product) throw new Error('product is not defined');

  failWhenSuspended(participation);

  const startTime = await constructValue(participation)
    .timespan()
    .then(slots => slots[0]?.start)
    .catch(() => undefined); // timeslot may have been deleted by admin

  // only check for signoff-block when termine are defined
  if (startTime) {
    const s = product.settings;
    const blockSignOffDaysInAdvance = s?.blockSignOffDaysInAdvance ?? 0;
    const lastSignOffDate = new Date(startTime.getTime() - blockSignOffDaysInAdvance * 24 * 60 * 60 * 1000);

    // check if signoff is allowed
    // allow admin to ignore blockfrist
    if (!throughAdmin) {
      if (lastSignOffDate < new Date()) {
        if (participation.state === ParticipationState.waiting)
          void 0; // always allow Signoff when on waiting-list
        else throw new BlockSignoffDaysInAdvanceError();
      }
    }
  }

  // handle payment
  const paymentPlugin = getDbPluginForScope('payment', product.institutionID);
  await paymentPlugin.cancleEnrollment(participation._id);

  // send cancel mail
  const { notifyAbmeldung } = await import('../notification/abmeldung.js');
  await notifyAbmeldung({ participationID: participation._id, throughAdmin });

  // set cancel status
  if (throughAdmin) {
    bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = true;
    participation.lastStateChangeWasInitiatedByAdmin = true;
  }

  // admin should be able to set state manually
  if (!throughAdmin) {
    participation.state = ParticipationState.left;
    // A user self-signoff (state → left) is a real change the admin should see as "letzte Änderung am".
    // saveEntityData bypasses transformBeforeUpdate, so bump updatedAt explicitly. The admin path
    // (throughAdmin) sets state + updatedAt itself in the trailing bulk write of
    // `_updateParticipationStateAsAdminUser`, so here we only clear the waiting-list place for it.
    (participation as { updatedAt?: Date }).updatedAt = new Date();
  }

  participation.waitingListPlace = null;
  await saveEntityData(participation);

  return 1;
}

export const moveUpFromWaitinglistIntoParticipating = registerMutation({
  operationName: 'moveUpFromWaitinglistIntoParticipating',
  params: [
    { name: 'participationID', type: () => String },
    { name: 'paymentMethod', type: () => String },
  ],
  type: () => BookingResult,
  resolve:
    (participationID: string, paymentMethod: Maybe<string>) =>
    async (gqlFields: BaseQLSelectionSet<BookingResult>): Promise<BookingResult> => {
      const participation = await loadRawEntityDataByIDOrFail(Participation, participationID, {
        _id: 1,
        _class: 1,
        state: 1,
        productID: 1,
        userID: 1,
        waitingListPlace: 1,
        currentPaymentID: 1,
        updatedAt: 1,
      });

      const workshop = await loadRawEntityDataByIDOrFail(Workshop, participation.productID, {
        _id: 1,
        _class: 1,
        institutionID: 1,
        capacity: 1,
        hasPrice: 1,
        appointments: { _id: 1, _class: 1, start: 1, end: 1, location: 1, meetingLink: { link: 1 } },
      });

      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        const isParticipant = currentUser._id === participation.userID;
        const canManageParticipation = currentUser.hasPermission(
          Permission.MANAGE_PARTICIPATION,
          workshop.institutionID,
        );
        if (!isParticipant && !canManageParticipation) throw new ExpectedError('not authorized');
        if (isParticipant && !canManageParticipation && participation.state !== ParticipationState.in) {
          const { assertBookingEmailConfirmed, assertWorkshopRegistrationOpen } = await import('./booking.js');
          const bookingContext = {
            serverContext: context.serverContext,
            impersonatingUser: (context as typeof context & { impersonatingUser?: string }).impersonatingUser,
          };
          await assertBookingEmailConfirmed(participation.userID, bookingContext);
          await assertWorkshopRegistrationOpen([participation.productID], bookingContext);
        }
      }

      const user = await loadRawEntityDataByIDOrFail(User, participation.userID);

      if (participation.state === ParticipationState.in)
        return new BookingResult({ loginToken: '', participationIDs: [participation._id], paymentRequired: false });
      if (participation.state !== ParticipationState.waiting)
        throw new ExpectedError('unexpected Participationstate: ' + participation.state);

      const paymentRequired = workshop.hasPrice;
      if (paymentRequired) {
        if (!paymentMethod) throw new Error('payment required but paymentmethod is undefined');
      }

      const leftCapacity = await calculateWorkshopLeftCapacity(workshop);
      if (!bhBaseQlContext.ignoreCapacity && leftCapacity === 0) {
        throw new Error('there are no free spots left');
      }

      participation.state = ParticipationState.in;
      participation.waitingListPlace = null;
      // Moving up from the waiting list is a real status change the admin must see as "letzte
      // Änderung am" — saveEntityData bypasses transformBeforeUpdate, so set updatedAt explicitly.
      participation.updatedAt = new Date();
      await saveEntityData(participation);

      // notify plugin
      const institutionID = workshop.institutionID;
      const paymentPlugin = getDbPluginForScope('payment', institutionID);
      await paymentPlugin.afterEnrollment(participation._id);

      // prepare result
      const participationIDs = [participation._id];
      const result = new BookingResult({ loginToken: '', participationIDs, paymentRequired });

      if (workshop.hasPrice) {
        if (!paymentMethod) throw new Error('unreachable');
        const paymentIDs = await createPayment(institutionID, [participation._id], paymentMethod);
        if (paymentIDs.length !== 1)
          throw new Error('expected 1 newly created Payment, but ' + paymentIDs.length + ' ids were returned');

        result.paymentIDs = paymentIDs;
      }

      await notifyNachrueckplatzErhalten(institutionID, user, [participation]);

      // send meeting info mail(s) for late joiners — the helper covers both the first and
      // second meeting-info mail. Matches the behaviour of the admin-enrollment and
      // self-registration paths.
      sendMeetingInfoMailsIfAlreadySent(participation._id, workshop._id);

      logger.info({ userID: user._id }, 'moved user up from waitinglist to participant');

      return result;
    },
});

export async function getProductPriceForUser(productID: string, userID: string) {
  const product = await loadRawEntityDataByIDOrFail(Product, productID, { hasPrice: 1, institutionID: 1 });
  if (!product.hasPrice) return null;
  const paymentPlugin = getDbPluginForScope('payment', product.institutionID);
  return paymentPlugin.getProductPrice(productID, userID);
}

export async function suspendParticipationsByUserInstitution(
  userID: string,
  suspension: NarrowedEntityData<Suspension, { institutionID: 1; blockedNoLimit: 1; blockedTill: 1 }>,
  forceSuspendParticipations: string[] = [],
  now: Date = new Date(),
) {
  const institutionID = suspension.institutionID;

  const collection = mongo().collection<Participation>(Participation._collectionName);
  const result = await collection.raw
    .aggregate<Participation>([
      { $match: { userID } },
      // Project the looked-up product to only the fields the institution filter and
      // getParticipationTimespan need, instead of pulling whole product documents through the pipeline.
      {
        $lookup: lookupEntityById(Product, 'productID', 'product', {
          _id: 1,
          institutionID: 1,
          appointments: { start: 1, end: 1 },
          slots: { _id: 1, start: 1, end: 1 },
        } as any),
      },
      { $unwind: '$product' },
      { $match: { 'product.institutionID': institutionID } },
    ])
    .toArray();

  const participationIDs = result
    .filter(participation => {
      if (forceSuspendParticipations.includes(participation._id)) {
        return true;
      }

      // do not suspend when participation is already completed
      if (participation.state === ParticipationState.participated) {
        return false;
      }

      // do not supspend when participation is already over
      const { start, end } = getParticipationTimespan(participation as any);
      if (end && end < now) {
        return false;
      }

      if (!suspension.blockedNoLimit) {
        const blockedTill = suspension.blockedTill;
        if (!blockedTill) throw new Error('blockedTill should be set but is not');
        if (start && blockedTill < start) {
          // suspension will be over before participation starts
          return false;
        }
      }

      return true;
    })
    .map(participation => participation._id);

  await mongoUpdateMany(
    Participation,
    { _id: { $in: participationIDs } },
    { $set: { state: ParticipationState.suspended, waitingListPlace: null, updatedAt: new Date() } },
  );
  await notifyNuzterSperrung(participationIDs);
}

export const getParticipationsByProductIDsAndState = registerQuery({
  operationName: 'getParticipationsByProductIDsAndState',
  type: () => [Participation],
  params: [
    { name: 'productIDs', type: () => [String] },
    { name: 'states', type: () => [ParticipationState] },
  ],
  resolve: (
    productIDs: string[],
    states: ParticipationState[] = [ParticipationState.in, ParticipationState.waiting],
  ) => {
    return async (gqlFields: BaseQLSelectionSet<Participation>) => {
      const collection = await scopedCollection<Participation>(Participation._class);
      const result = await collection
        .find<Participation>({ productID: { $in: productIDs }, state: { $in: states } })
        .toArray();
      return result;
    };
  },
});

export const cancelEnrollment = registerQuery({
  operationName: 'cancelEnrollment',
  type: () => Int,
  params: [
    { name: 'participationID', type: () => String },
    { name: 'throughAdmin', type: () => Boolean },
  ],
  resolve: (participationID: string, throughAdmin: boolean) => {
    return async () => {
      const _user = await getCurrentUserServer();
      if (throughAdmin && !(_user as User).isAdmin) throw new Error('action not permitted');

      // check if cancel possible
      const participation = await loadRawEntityDataByIDOrFail(Participation, participationID, {
        _id: 1,
        _class: 1,
        userID: 1,
        productID: 1,
        state: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        waitingListPlace: 1,
        nachrueckPriority: 1,
      });

      if (!throughAdmin && participation.userID !== _user?._id) {
        throw new ExpectedError('action not permitted 2');
      }

      const user = await loadRawEntityDataByIDOrFail(User, participation.userID, {
        _id: 1,
        _class: 1,
        suspentions: { institutionID: 1 },
      });

      const product = await loadRawEntityDataByIDOrFail(Product, participation.productID, { _id: 1, institutionID: 1 });

      const settings = await getMergedProductSettings(participation.productID, ['blockSignOffDaysInAdvance']);

      logger.info({ participation, throughAdmin }, 'cancelEnrollment');
      const m = await import('../api/participation.js');
      return m._cancelEnrollment({ ...participation, user, product: { ...product, settings } }, throughAdmin);
    };
  },
});

export async function cancelSuspensionParticipationsByUserInstitution(userID: string, institutionID: string) {
  const collection = mongo().collection<Participation>(Participation._collectionName);
  const result = await collection.raw
    .aggregate<Participation>([
      { $match: { userID, state: ParticipationState.suspended } },
      { $lookup: { from: Product._collectionName, localField: 'productID', foreignField: '_id', as: 'product' } },
      { $unwind: '$product' },
      { $match: { 'product.institutionID': institutionID } },
    ])
    .toArray();

  const user = await getUserByID(userID)({
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });
  if (!user) return;

  for (const participation of result) {
    const constructedParticipation = constructValue(participation) as Participation;
    constructedParticipation.state = ParticipationState.left;
    await constructedParticipation.save();
  }

  await notifyNuzterSperrungAufgehoben(result.map(participation => participation._id));
}

export const countMatchingParticipations = registerQuery({
  operationName: 'countMatchingParticipations',
  type: () => Int,
  params: [
    { name: 'participated', type: () => Boolean },
    { name: 'institutionID', type: () => String },
    { name: 'timeframeID', type: () => String },
  ],
  resolve: (participated, institutionID, timeframeID) => {
    return async () => {
      const collection = mongo().collection<Participation>(Participation._collectionName);

      const result = await collection.raw
        .aggregate([
          {
            $match: {
              ...(typeof participated === 'boolean'
                ? { state: participated ? ParticipationState.participated : ParticipationState.in }
                : {}),
            },
          },
          { $lookup: { from: 'products', localField: 'productID', foreignField: '_id', as: 'product' } },
          {
            $match: {
              ...(institutionID ? { 'product.institutionID': institutionID } : {}),
              ...(timeframeID ? { 'product.timeframeID': timeframeID } : {}),
            },
          },
          { $count: 'total' },
        ])
        .toArray();

      return result[0]?.total ?? 0;
    };
  },
});

export function shouldSendParticipationMailAfterParticipationParticipatedGotUpdated(
  participation: NarrowedEntityData<
    Participation,
    { state: 1; product: { settings: { autoSendCertifatesEnabled: 1 }; attachedCertificate: { certificateID: 1 } } }
  >,
) {
  // always send not participatied mail
  if (participation.state !== ParticipationState.participated) {
    return { shouldSendMail: true, mail: 'nutzer-teilnahme-aufgehoben' as const };
  }

  const autoSendCertifatesEnabled = !!participation.product?.settings?.autoSendCertifatesEnabled;
  const certificateExists = !!participation.product?.attachedCertificate?.certificateID;

  if (autoSendCertifatesEnabled && certificateExists)
    return { shouldSendMail: true, mail: 'nutzer-teilnahme' as const };

  if (autoSendCertifatesEnabled && !certificateExists)
    return { shouldSendMail: true, mail: 'nutzer-teilnahme-without-certificate' as const };

  if (!autoSendCertifatesEnabled)
    return { shouldSendMail: true, mail: 'nutzer-teilnahme-without-certificate' as const };

  throw new Error('unreachable');
}

export async function getAllowedParticipations(user: ServerCachedUser) {
  const userIDs = await getAllowedUsers(user);
  const aggregation = [{ $match: { userID: { $in: userIDs } } }, { $group: { _id: null, ids: { $push: '$_id' } } }];

  const participationCol = mongo().collection(Participation._collectionName);
  let result = await participationCol
    .aggregate(aggregation)
    .toArray()
    .then(res => res.at(0)?.ids ?? []);

  if (user.isLecturer) {
    const lecturerContexts = lecturerContextsOf(user.roles);
    const teachingsCol = mongo().collection(Teaching._collectionName);
    const aggregation = [
      { $match: { lecturerID: user._id } },
      // only workshops of institutions where the user is a lecturer; in all others they are a referent
      ...(lecturerContexts
        ? [
            { $lookup: { from: Product._collectionName, as: 'product', localField: 'productID', foreignField: '_id' } },
            { $match: { 'product.institutionID': { $in: lecturerContexts } } },
          ]
        : []),
      { $lookup: { from: 'participations', as: 'participation', localField: 'productID', foreignField: 'productID' } },
      { $unwind: '$participation' },
      { $group: { _id: null, participationIDs: { $addToSet: '$participation._id' } } },
    ];

    const stuff = await teachingsCol.aggregate(aggregation).toArray();
    result = result.concat(stuff?.[0]?.participationIDs ?? []);
  }

  return result;
}

export const setIkhDoNotSendInvoice = registerQuery({
  operationName: 'setIkhDoNotSendInvoice',
  type: () => Int,
  params: [
    { name: 'participationID', type: () => String },
    { name: 'doNotSendInvoice', type: () => Boolean },
  ],
  resolve: (participationID: string, doNotSendInvoice: boolean) => {
    return async () => {
      const participation = await loadRawEntityDataByIDOrFail(Participation, participationID, { productID: 1 });
      const product = await loadRawEntityDataByIDOrFail(Product, participation.productID, { institutionID: 1 });
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID))
          throw new ExpectedError('not authorized');
      }

      const c = mongo().collection<Participation>(Participation._collectionName);
      await c.updateOne(
        { _id: participationID },
        { $set: { ikh_doNotSendInvoice: doNotSendInvoice, updatedAt: new Date() } },
      );
      return 1;
    };
  },
});

export const setIkhDoNotSendInvoiceBulk = registerMutation({
  operationName: 'setIkhDoNotSendInvoiceBulk',
  type: () => Int,
  params: [
    { name: 'participationIDs', type: () => [String] },
    { name: 'doNotSendInvoice', type: () => Boolean },
  ],
  resolve: (participationIDs: string[], doNotSendInvoice: boolean) => async () => {
    if (context.serverContext) return 0;
    const { collection, productID, uniqueIDs } = await getAuthorizedBulkParticipations(participationIDs, [
      Permission.MANAGE_PARTICIPATION,
    ]);

    const result = await collection.updateMany(
      { _id: { $in: uniqueIDs }, productID },
      { $set: { ikh_doNotSendInvoice: doNotSendInvoice, updatedAt: new Date() } },
    );
    return result.modifiedCount;
  },
});

export const getUserTimeSlotsOfActiveParticipations = registerSubscription({
  operationName: 'getUserTimeSlotsOfActiveParticipations',
  type: () => [TimeSlot],
  params: [{ name: 'userID', type: () => String }],
  middlewares: [checkAuthorizedMiddleware(({ user, params }) => user._id === params.userID)],
  resolve: (userID: string) => {
    return (gqlFields: BaseQLSelectionSet<TimeSlot>): Observable<TimeSlot[]> => {
      const participations$ = watchQuery(Participation._collectionName, {
        userID,
        state: { $in: [ParticipationState.participated, ParticipationState.in] },
      });

      return participations$.pipe(
        switchMap(participationData => {
          return getParticipationsByID(participationData?.map(pd => pd._id) ?? [])({
            _id: 1,
            _class: 1,
            productID: 1,
            timeslotID: 1,
            product: {
              _id: 1,
              appointments: {
                _id: 1,
                start: 1,
                end: 1,
                isNonBlocking: 1,
                location: 1,
                name: 1,
                meetinLink: { link: 1 },
              },
              slots: { _id: 1, start: 1, end: 1, isNonBlocking: 1 },
            },
          } as any);
        }),
        switchMap(participations => {
          return Promise.all(participations.map(p => p!.timespan() as Promise<TimeSlot[]>));
        }),
        map(deepSlots => deepSlots.flat()),
      );
    };
  },
});

export const addUserManuallyToWorkshop = registerMutation({
  operationName: 'addUserManuallyToWorkshop',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'productID', type: () => String },
    { name: 'participated', type: () => Boolean },
    { name: 'timeslotID', type: () => String },
  ],
  resolve: (userID: string, productID: string, participated: boolean, timeslotID: Maybe<string>) => {
    return async () => {
      const product = await getProductByID(productID)({ _class: 1, institutionID: 1 });
      if (!product) throw new Error('product not found ' + productID);

      // check that user exists
      const user = await loadRawEntityDataByID(User, userID);
      if (!user) throw new Error('user not found ' + userID);

      const currentUser = await getCurrentUserServer();
      if (!currentUser || !currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_USERS)) {
        throw new Error('permission denied');
      }

      const participation = await (async () => {
        if (product._class === 'Workshop') {
          return new ParticipationWorkshop({
            userID: user._id,
            productID,
            state: participated ? ParticipationState.participated : ParticipationState.in,
            lastStateChangeWasInitiatedByAdmin: true,
            nachrueckPriority: await getWaitinglistPriorityForUserForProduct(user._id, productID),
          });
        }

        if (product._class === 'Einzelberatung') {
          return new ParticipationEinzelberatung({
            userID: user._id,
            productID,
            state: participated ? ParticipationState.participated : ParticipationState.in,
            lastStateChangeWasInitiatedByAdmin: true,
            timeslotID,
          });
        }

        throw new Error('unknown product class ' + product._class);
      })();

      bhBaseQlContext.participationStateChangeWasInitiatedByAdmin = true;
      await participation.create();
      await runInContext(serverContext.sessionID, () => notifyVeranstaltungseintragungDurchAdmin(participation._id));

      // send meeting info mail if it was already sent to other participants
      if (!participated) {
        if (product._class === 'Workshop') {
          sendMeetingInfoMailsIfAlreadySent(participation._id, productID);
        } else if (product._class === 'Einzelberatung') {
          sendEinzelberatungMeetingInfoMailsIfAlreadySent(participation._id, productID, timeslotID);
        }
      }
    };
  },
});

export const getParticipationsByIDs = registerQuery({
  operationName: 'getParticipationsByIDs',
  type: () => [Participation],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Participation>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Participation, ids, gqlFields),
});

export const subscribeParticipationsByIDs = registerSubscription({
  operationName: 'subscribeParticipationsByIDs',
  type: () => [Participation],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Participation>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Participation, ids, gqlFields),
});

export const getAllParticipations = registerQuery({
  operationName: 'getAllParticipations',
  type: () => [Participation],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Participation>>(gqlFields: SelSet) =>
      readAllEntities(Participation, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllParticipations = registerSubscription({
  operationName: 'subscribeAllParticipations',
  type: () => [Participation],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Participation>>(gqlFields: SelSet) =>
      watchAllEntities(Participation, gqlFields, { skip, limit, excludeIDs }),
});

export const getParticipationByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Participation>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getParticipationsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeParticipationByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Participation>>(gqlFields: SelSet) =>
    id ? subscribeParticipationsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
